import { useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Pencil, Plus } from 'lucide-react';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SortableList } from '@/components/ui/SortableList';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormLayout';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';

const TablesDoc = graphql(/* GraphQL */ `
  query Tables {
    tables(includeInactive: true) {
      id
      name
      sort
      isActive
    }
  }
`);

const CreateTableDoc = graphql(/* GraphQL */ `
  mutation CreateTable($input: CreateTableInput!) {
    createTable(input: $input) {
      id
      name
      sort
      isActive
    }
  }
`);

const UpdateTableDoc = graphql(/* GraphQL */ `
  mutation UpdateTable($id: ID!, $input: UpdateTableInput!) {
    updateTable(id: $id, input: $input) {
      id
      name
      isActive
    }
  }
`);

const ReorderDoc = graphql(/* GraphQL */ `
  mutation ReorderTables($entity: SortableEntity!, $orderedIds: [ID!]!) {
    reorder(entity: $entity, orderedIds: $orderedIds)
  }
`);

const ExportTablesCsvDoc = graphql(/* GraphQL */ `
  query ExportTablesCsv {
    exportTablesCsv {
      url
      expiresAt
    }
  }
`);

interface TableRow {
  id: string;
  name: string;
  sort: number;
  isActive: boolean;
}

export function TablesListPage() {
  const apollo = useApolloClient();
  const { data, previousData, loading, refetch } = useQuery(TablesDoc);
  const tablesData = data ?? previousData;
  const [createTable, { loading: creating }] = useMutation(CreateTableDoc);
  const [updateTable] = useMutation(UpdateTableDoc);
  const [reorder] = useMutation(ReorderDoc);

  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<TableRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);

  const tables: TableRow[] = useMemo(
    () =>
      (tablesData?.tables ?? []).map((t) => ({
        id: t.id,
        name: t.name,
        sort: t.sort,
        isActive: t.isActive,
      })),
    [tablesData],
  );

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    await createTable({ variables: { input: { name, isActive: true } } });
    setNewName('');
    await refetch();
  };

  const handleReorder = async (orderedIds: string[]) => {
    await reorder({ variables: { entity: 'TABLE', orderedIds } });
    await refetch();
  };

  const openEdit = (row: TableRow) => {
    setEditing(row);
    setEditName(row.name);
    setEditActive(row.isActive);
  };

  const submitEdit = async () => {
    if (!editing || savingEdit) return;
    setSavingEdit(true);
    try {
      await updateTable({
        variables: {
          id: editing.id,
          input: { name: editName, isActive: editActive },
        },
      });
      setEditing(null);
      await refetch();
    } finally {
      setSavingEdit(false);
    }
  };

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportTablesCsvDoc,
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportTablesCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportTablesCsv.url);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Tables
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Drag to reorder. Open Edit to change visibility on the floor plan;
            inactive tables keep their history.
          </p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      <Card>
        <form onSubmit={handleAdd} className="flex items-end gap-3">
          <div className="flex-1">
            <FormField label="New table">
              <Input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="e.g. Terrace 5"
              />
            </FormField>
          </div>
          <Button
            type="submit"
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={creating || !newName.trim()}
          >
            Add
          </Button>
        </form>
      </Card>

      <Card padded={false}>
        {loading && tables.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Loading tables…</p>
        ) : tables.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">
            No tables yet. Add one above to populate the floor plan.
          </p>
        ) : (
          <div className="p-4">
            <SortableList
              items={tables}
              onReorder={handleReorder}
              renderItem={(item, { dragHandle }) => (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-gray-900">
                  {dragHandle}
                  <span className="flex-1 truncate font-medium text-gray-900 dark:text-white">
                    {item.name}
                  </span>
                  <Badge tone={item.isActive ? 'success' : 'neutral'}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Pencil className="h-4 w-4" />}
                    onClick={() => openEdit(item)}
                  >
                    Edit
                  </Button>
                </div>
              )}
            />
          </div>
        )}
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => (savingEdit ? null : setEditing(null))}
        title="Edit table"
        footer={
          <>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setEditing(null)}
              disabled={savingEdit}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitEdit}
              disabled={savingEdit || !editName.trim()}
            >
              {savingEdit ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input
              value={editName}
              onChange={(event) => setEditName(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Active">
            <Switch
              checked={editActive}
              onChange={(event) => setEditActive(event.target.checked)}
              label={editActive ? 'Visible on floor plan' : 'Hidden'}
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
